/**
 * Approved copy, lifted from COPY.md. Text lives here rather than inline in
 * components so it can be reviewed, diffed and eventually translated as prose
 * rather than as JSX.
 */

export const site = {
    name: 'dnsleaktest.org',
    title: 'DNS Leak Test — see which resolvers answer your DNS queries',
    description:
        'Free DNS leak test. Find out which DNS resolvers actually answer your lookups, ' +
        'who operates them, and whether your VPN is leaking.',
    nav: [
        { to: '/', label: 'Home' },
        { to: '/dns-leak-test', label: 'Leak test', primary: true },
        { to: '/fixing-dns-leak', label: 'Fixing leaks' },
        { to: '/dns-over-https', label: 'DNS-over-HTTPS' },
        { to: '/doh-resolvers', label: 'DoH servers' },
        { to: '/faq', label: 'FAQ' },
    ],
    footer: {
        copyright: '© 2020–2026 DEF24 Inc. All rights reserved.',
        tagline: 'An information security service.',
        privacy: 'Privacy policy',
        faq: 'FAQ',
    },
    // our sibling IP-lookup service; linked from the connection panel and the
    // footer. rel keeps referrer/follow so it works as a real back-link.
    partner: {
        href: 'https://ip8.com',
        label: 'ip8.com',
        connectionLabel: 'Full IP report on ip8.com',
        footerNote: 'Our IP lookup tool',
    },
    maintenance: 'The service is temporarily unavailable. Please try again shortly.',
} as const

export const home = {
    heading: 'DNS leak test',
    lede: 'Find out which DNS resolvers actually answer your lookups — and who operates them.',
    cta: 'Run leak test',
    ctaNote: '50 lookups · about 30 seconds',
    connectionPanel: 'Your connection',
    privacy: {
        heading: 'DNS privacy',
        body: [
            'DNS is as old as the internet itself. The internet runs on IP addresses, but almost nothing works without DNS translating names into them first — it is the lookup that happens before every connection you make.',
            'Despite that, most DNS traffic still travels without encryption or privacy. Every network between you and your resolver can see which sites you are about to visit, and may keep that record. That opens the door to profiling, DNS spoofing, man-in-the-middle attacks, filtering and phishing.',
            'Your browsing history is worth protecting. It starts with knowing where your DNS queries actually go.',
        ],
    },
    whatIsALeak: {
        heading: 'What is a DNS leak?',
        body: [
            'A DNS leak is when your DNS queries go somewhere you did not choose.',
            'At some point you have to trust someone with your lookups — your ISP, your VPN provider, or whoever runs the resolver you picked. Even with recent improvements, that operator can still read your queries. The problem is not that someone sees them; it is that the wrong someone does.',
            'DNS settings live in several places at once: your router, your operating system, your browser, sometimes individual apps. When they disagree, some of your queries quietly go somewhere else. That is a leak, and misconfiguration, badly behaved software, or a network deliberately intercepting DNS can all cause it.',
            'You chose your DNS provider. This test tells you whether your choice is actually being honoured.',
        ],
    },
    whyUs: {
        heading: 'Why us?',
        body: [
            'Most DNS leak tests are run by VPN companies or their affiliates, and they all reach the same conclusion: buy a VPN. We are not selling one.',
            'A DNS leak is a technical problem, and the technical answer is usually a settings change, not a subscription. Your DNS history is an X-ray of your browsing — it is worth understanding rather than outsourcing.',
            'Speed is the usual reason given for choosing a resolver. It matters, but it is half the story. Trust is the other half.',
        ],
    },
} as const

export const leakTest = {
    headingFor: (ip: string) => `DNS leak test for ${ip}`,
    sharedHeadingFor: (ip: string) => `Shared DNS leak test for ${ip}`,
    stop: 'Stop and show results',
    running: 'Running',
    verdict: {
        ok: (n: number, org: string) =>
            n === 1
                ? `Your one resolver belongs to ${org}, matching your connection.`
                : `All ${n} resolvers belong to ${org}, matching your connection.`,
        // Neutral by design: sending DNS to a third party -- Cloudflare, Quad9,
        // your chosen provider -- is common and usually deliberate, not a leak.
        warn: (orgs: number, countries: number) =>
            `Your queries were answered by ${orgs} organisation${orgs === 1 ? '' : 's'} in ${countries} ${countries === 1 ? 'country' : 'countries'}. That is normal if you chose them.`,
        leak: (exitOrg: string, n: number, ispOrg: string) =>
            `You connect via ${exitOrg}, but ${n} of your resolvers belong${n === 1 ? 's' : ''} to ${ispOrg} — your ISP, not your VPN.`,
        unknown: 'Not enough information to judge this result.',
        // 'warn' is not called a leak: it describes third-party DNS rather than
        // alarming about it, since without knowing the visitor's intent it cannot
        // be called a leak. Only 'leak' -- an exit/DNS mismatch -- gets that word.
        labels: { ok: 'No leak detected', warn: 'Third-party DNS', leak: 'DNS leak detected', unknown: 'Inconclusive' },
    },
    resolvers: {
        heading: 'Resolvers answering for you',
        body: [
            'We send 50 lookups to a unique subdomain and record every resolver that asks about it. That is deliberately aggressive: we want to surface resolvers that only handle part of your traffic.',
            'Large providers such as Google and Cloudflare operate thousands of relays, so seeing several addresses from one of them is normal. What matters is how many organisations appear, not how many IP addresses.',
            'Some leaks are intermittent. If a result looks clean but you suspect otherwise, run the test again at a different time of day.',
        ],
    },
    understanding: {
        heading: 'Understanding your results',
        body: [
            'Compare what you see against what you expect.',
            "If you use your ISP's resolvers — the default for most people — you should see only those. If you are on a VPN, you should see only your VPN provider's. Seeing both is a leak.",
            'Location matters as much as ownership. If you connect through a VPN server in the UK but your resolvers are in the US, every site you visit can infer the mismatch. Your IP says one country, your DNS says another.',
            'Some VPN providers route DNS to third parties rather than running their own resolvers. That is worth knowing before you trust one, and it is not something they usually advertise.',
            'Catching every case takes some familiarity with how DNS is configured on your system, but this test will surface the common ones. Run it more than once — intermittent leaks tend to appear under load, when software falls back to a secondary resolver.',
        ],
    },
    preventing: {
        heading: 'Preventing leaks',
        body: 'Fixing a leak is usually harder than finding one, because DNS settings are spread across your router, operating system, browser and sometimes individual apps.',
        linkLabel: 'Fixing DNS leaks',
    },
    emptyShare: {
        heading: 'This shared result could not be found.',
        body: 'The link may be incomplete, or the result may have expired.',
    },
    saved: {
        heading: 'Your saved tests',
        note: 'Kept in this browser only. Clearing site data removes them; the shared results themselves stay reachable by link.',
        forget: 'Forget',
    },
    actions: { retest: 'Retest', share: 'Share', shared: 'Link copied', save: 'Save', saved: 'Saved', export: 'Export' },
    share: {
        title: 'Share this result',
        intro: 'Publishing creates a public link anyone can open. Nothing is shared until you press the button.',
        create: 'Create share link',
        creating: 'Creating…',
        copy: 'Copy link',
        copied: 'Link copied',
        alsoSave: 'Also save to this browser',
        saved: 'Saved to this browser',
        error: 'Could not create a share link. Please try again.',
    },
} as const

export const dohResolvers = {
    heading: 'Public DoH resolvers',
    lede: 'Every public DNS-over-HTTPS resolver we can reach, revalidated continuously.',
    intro: [
        'We probe each endpoint on a schedule and keep the ones that answer. For every resolver that passes you get where it is, how fast it replied to our probe, and whether a browser can use it at all.',
        'Choosing a resolver is a question of trust as much as speed. This list tells you who is available and where they sit; it does not tell you who deserves your queries. Somebody sees every domain you look up — decide who that should be.',
    ],
    error: 'Could not load the resolver directory. It may be warming up — try again in a moment.',
    queue: {
        hint: 'Latency here was measured from our probe. Queue any resolver to measure it from your own browser on the DNS-over-HTTPS page.',
        count: (n: number, max: number) =>
            n >= max
                ? `${n} queued (the most recent ${max} are kept) —`
                : `${n} queued for testing —`,
        action: 'test them',
    },
    freshness: (when: string) => `Directory last revalidated ${when}. Your copy is cached in this tab for an hour.`,
    about: {
        heading: 'How this list is built',
        body: [
            'Each endpoint is queried directly. A resolver appears here only if its most recent check answered a real DNS question — resolvers that time out, refuse, or fail TLS are dropped until they recover.',
            'The CORS column matters if you want to use a resolver from a web page: without those headers a browser will not let a script read the answer, even though the endpoint works fine from a command line or an operating-system resolver.',
            'Latency is a single sample measured from our probe, not from you. Treat it as a rough ordering — your own distance to a resolver dominates it. The DNS-over-HTTPS page measures latency from your browser instead.',
        ],
    },
} as const

export const doh = {
    heading: 'DNS-over-HTTPS',
    intro: [
        'DoH sends DNS queries over an encrypted HTTPS connection instead of plain UDP port 53. Firefox and Chrome have pushed it hard, and it closes real problems: your queries can no longer be read or altered in transit by the networks they pass through.',
        'What it does not change is who sees them at the other end. Your DoH provider still observes every domain you look up, along with your IP address, location and the time — and can still retain it. Encryption moves the trust, it does not remove it.',
        'Browsers increasingly make this choice for you by defaulting to their own resolver. We think that decision should be yours.',
    ],
    speedIntro: [
        'Latencies below are measured from your browser, one sample each, and include the TLS handshake. Treat them as relative to one another, not as absolute times — your distance to each resolver dominates the result.',
        'Speed is the easy thing to measure. Trust is the thing that matters.',
    ],
    form: { label: 'Hostname to look up', defaultHost: 'google.com', test: 'Test', add: 'Add resolver' },
    unreachable: 'unreachable from your network',
    setup: {
        heading: 'Enable DoH in your browser',
        body: 'Most browsers can send DNS queries over HTTPS. Pick yours below — each walkthrough is about twenty seconds.',
    },
    dialog: {
        title: 'Add a DoH resolver',
        name: 'Provider name',
        url: 'Endpoint URL',
        cancel: 'Cancel',
        save: 'Save',
        errors: {
            hostname: 'Enter a valid hostname',
            url: 'Enter a valid URL',
            minLength: (min: number) => `Must be at least ${min} characters`,
            required: 'This field is required',
        },
    },
} as const

export const faq = {
    heading: 'DNS questions, answered',
    lede: 'What a leak is, why a VPN can still have one, and what encrypted DNS does and does not hide — short answers, no sales pitch.',
    searchLabel: 'Search the FAQ',
    searchPlaceholder: 'Search — leak, vpn, doh, router…',
    indexLabel: 'FAQ categories',
    noMatch: 'Nothing matches that.',
} as const
